import { useContext, useMemo } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { env } from "../../../../../../env";
import { useGetStripeClientSecret } from "../../../../../../apis/billingApi.hooks";
import { AppContext } from "../../../../../../context/app";
import PaymentComponent from "./PaymentComponent";
import StripeLoadingScreen from "./StripeLoadingScreen";
import ErrorScreen from "./ErrorScreen";
import AddCanadaBank from "./AddCanadaBank";
import {
  stripeAccountType,
  IsoCurrencyCodes,
} from "../../../../../../pages/Payment/types";
import type { RequestType } from "../Billing";

type AddPaymentMethodProps = {
  userUuid: string;
  requestType: RequestType;
  closeAddPaymentModal: () => void;
};

/*
  On load, we fetch stripeOptions from billing. Depending on the gym
  and the settings, there will either be platformStripeInfo or
  internationalStripeInfo needed to load the stripe elements.
*/
export default function AddPaymentMethod({
  userUuid,
  requestType,
  closeAddPaymentModal,
}: AddPaymentMethodProps) {
  const { client } = useContext(AppContext);
  const {
    data: stripeOptions,
    isFetching: isStripeOptionsFetching,
    error: stripeOptionsError,
    refetch: refetchStripeOptions,
  } = useGetStripeClientSecret(client.uuid, userUuid);

  // For US gyms or international without international debit enabled
  const platformStripeInfo = useMemo(() => {
    return stripeOptions?.customerDetails.find(
      (customerDetail) =>
        customerDetail.stripeAccountType === stripeAccountType.PLATFORM,
    );
  }, [stripeOptions]);

  const platformStripePromise = useMemo(() => {
    if (platformStripeInfo) {
      return loadStripe(env.STRIPE_PAYMENTS_PUBLISHABLE_KEY);
    }
  }, [platformStripeInfo]);

  // For gyms outside the US that have international debit enabled
  const internationalStripeInfo = useMemo(() => {
    return stripeOptions?.customerDetails.find(
      (customerDetail) =>
        customerDetail.stripeAccountType === stripeAccountType.CONNECT,
    );
  }, [stripeOptions]);

  // For international gyms we need to load stripe with a different publishable key
  const internationalStripePromise = useMemo(() => {
    if (internationalStripeInfo) {
      return loadStripe(internationalStripeInfo.stripePublishableKey);
    }
  }, [internationalStripeInfo]);

  const isCanadaGym = client.currencyIso === IsoCurrencyCodes.CANADA;
  const isUSGym = client.currencyIso === IsoCurrencyCodes.USA;

  const isBank = requestType === "bank";
  const isCard = requestType === "card";

  const isInternationalBankRequest =
    internationalStripeInfo && internationalStripePromise && !isUSGym && isBank;

  const isCardOrUSGymRequest =
    platformStripeInfo && platformStripePromise && (isUSGym || isCard);

  if (stripeOptionsError) {
    return <ErrorScreen refetch={refetchStripeOptions} />;
  }

  if (isStripeOptionsFetching) {
    return <StripeLoadingScreen />;
  }

  if (isInternationalBankRequest) {
    if (isCanadaGym) {
      return (
        <Elements
          stripe={internationalStripePromise}
          options={{
            clientSecret: internationalStripeInfo.clientSecret,
          }}
        >
          <AddCanadaBank
            userUuid={userUuid}
            clientUuid={client.uuid}
            stripeClientSecret={internationalStripeInfo?.clientSecret}
            stripeCustomer={internationalStripeInfo?.stripeCustomer}
            closeAddPaymentModal={closeAddPaymentModal}
          />
        </Elements>
      );
    }

    return (
      <Elements
        stripe={internationalStripePromise}
        options={{
          clientSecret: internationalStripeInfo.clientSecret,
        }}
      >
        <PaymentComponent
          userUuid={userUuid}
          clientUuid={client.uuid}
          stripeCustomer={internationalStripeInfo.stripeCustomer}
          closeAddPaymentModal={closeAddPaymentModal}
        />
      </Elements>
    );
  }

  if (isCardOrUSGymRequest) {
    return (
      <Elements
        stripe={platformStripePromise}
        options={{
          clientSecret: platformStripeInfo.clientSecret,
        }}
      >
        <PaymentComponent
          userUuid={userUuid}
          clientUuid={client.uuid}
          stripeCustomer={platformStripeInfo.stripeCustomer}
          requestType={requestType}
          closeAddPaymentModal={closeAddPaymentModal}
        />
      </Elements>
    );
  }

  return <ErrorScreen refetch={refetchStripeOptions} />;
}
